// Per-line text sizing for feed posts with a background (article.background /
// article.backgroundImage). The short text (article.ingress) stays a plain
// string; a line starting with '# ' renders large and a line starting with
// '-# ' renders small (Discord-style subtext). Rendered by
// components/gui/listFrontpage.js and previewed by components/article/edit.js.
//
// Instead of the legacy flat 150-character gate, the background is kept as
// long as the *weighted* length fits the budget: large characters cost 2,
// medium 1 and small 0.5 — so smaller text allows longer posts.

export const BACKGROUND_TEXT_BUDGET = 150;

const SIZE_PROPS = {
    l: { weight: 2, em: 3.5 },
    m: { weight: 1, em: 2.5 },
    s: { weight: 0.5, em: 1.5 },
};

const EMOJI_BOOST = 1.5;
const EMOJI_ONLY_MAX = 4;

// '# ' (heading, degrades to markdown h1) => large; '-# ' => small. The space
// is required so hashtags ('#skyting') are never treated as size markers.
const LARGE_MARKER = /^# /;
const SMALL_MARKER = /^-# /;

// One emoji "unit": a flag pair or pictograph, plus its variation selectors,
// skin tones and ZWJ-joined pictographs (family/profession sequences).
const EMOJI_UNIT = /(?:\p{Regional_Indicator}{2}|\p{Extended_Pictographic})(?:️|⃣|\p{Emoji_Modifier})*(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})*)*/gu;

function isEmojiOnly(text) {
    const stripped = (text || '').replace(/\s+/g, '');
    if (!stripped) {
        return false;
    }
    const units = stripped.match(EMOJI_UNIT) || [];
    if (units.length === 0 || units.length > EMOJI_ONLY_MAX) {
        return false;
    }
    return units.join('') === stripped;
}

export function parseBackgroundLines(text) {
    if (!text) {
        return [];
    }
    return text.split('\n').map((rawLine) => {
        let size = 'm';
        let line = rawLine;
        if (LARGE_MARKER.test(rawLine)) {
            size = 'l';
            line = rawLine.replace(LARGE_MARKER, '');
        } else if (SMALL_MARKER.test(rawLine)) {
            size = 's';
            line = rawLine.replace(SMALL_MARKER, '');
        }
        return { size, text: line, emojiOnly: isEmojiOnly(line) };
    });
}

// The composer stores line sizes as lightweight prefix markers for backwards
// compatibility, but its visual editor should only expose the post text.
export function plainBackgroundText(text) {
    return parseBackgroundLines(text).map(line => line.text).join('\n');
}

function serializeBackgroundLines(lines) {
    return lines.map((line) => {
        if (line.size === 'l') {
            return `# ${line.text}`;
        }
        if (line.size === 's') {
            return `-# ${line.text}`;
        }
        return line.text;
    }).join('\n');
}

// Preserve the size of unchanged lines while the marker-free composer text is
// edited. New/split lines inherit the size of the line being edited.
export function reconcileBackgroundText(plainText, previousText = '') {
    if (!plainText) {
        return '';
    }
    const previousLines = parseBackgroundLines(previousText);
    const nextTexts = plainText.split('\n');
    const nextSizes = new Array(nextTexts.length);
    let prefix = 0;
    while (prefix < previousLines.length && prefix < nextTexts.length
        && previousLines[prefix].text === nextTexts[prefix]) {
        nextSizes[prefix] = previousLines[prefix].size;
        prefix += 1;
    }

    let previousSuffix = previousLines.length - 1;
    let nextSuffix = nextTexts.length - 1;
    while (previousSuffix >= prefix && nextSuffix >= prefix
        && previousLines[previousSuffix].text === nextTexts[nextSuffix]) {
        nextSizes[nextSuffix] = previousLines[previousSuffix].size;
        previousSuffix -= 1;
        nextSuffix -= 1;
    }

    const inheritedSize = previousLines[prefix]?.size
        || previousLines[Math.max(0, prefix - 1)]?.size
        || 'm';
    for (let idx = prefix; idx <= nextSuffix; idx += 1) {
        nextSizes[idx] = inheritedSize;
    }

    return serializeBackgroundLines(nextTexts.map((text, idx) => ({
        text,
        size: nextSizes[idx] || 'm',
    })));
}

function lineIndexAt(text, position) {
    return text.slice(0, Math.max(0, position)).split('\n').length - 1;
}

export function backgroundLineSizeAt(text, plainPosition = 0) {
    const lines = parseBackgroundLines(text);
    return lines[lineIndexAt(plainBackgroundText(text), plainPosition)]?.size || 'm';
}

// Apply a size to all marker-free lines touched by the current selection and
// return the compatible marker-encoded value for persistence.
export function setBackgroundLineSize(text, selectionStart, selectionEnd, size) {
    const lines = parseBackgroundLines(text);
    if (lines.length === 0) {
        return '';
    }
    const plainText = plainBackgroundText(text);
    const startLine = lineIndexAt(plainText, selectionStart);
    const inclusiveEnd = selectionEnd > selectionStart && plainText[selectionEnd - 1] === '\n'
        ? selectionEnd - 1
        : selectionEnd;
    const endLine = lineIndexAt(plainText, inclusiveEnd);
    return serializeBackgroundLines(lines.map((line, idx) => ({
        text: line.text,
        size: idx >= startLine && idx <= endLine ? size : line.size,
    })));
}

// Visible-symbol length: an emoji, a flag or a ZWJ family costs one, so the
// budget meter matches what the author sees. Intl.Segmenter is missing in
// older Firefox — code points are the fallback (ZWJ sequences then cost more).
const GRAPHEME_SEGMENTER = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter('nb', { granularity: 'grapheme' })
    : null;

export function textLength(text) {
    if (!text) {
        return 0;
    }
    if (GRAPHEME_SEGMENTER) {
        return Array.from(GRAPHEME_SEGMENTER.segment(text)).length;
    }
    return Array.from(text).length;
}

// Weighted character count: marker characters are free, line breaks cost 1
// (matching the legacy raw-length gate for marker-less posts).
export function weightedLength(text) {
    const lines = parseBackgroundLines(text);
    if (lines.length === 0) {
        return 0;
    }
    const chars = lines.reduce((sum, line) => sum + (textLength(line.text) * SIZE_PROPS[line.size].weight), 0);
    return chars + (lines.length - 1);
}

export function hasSizeMarkers(text) {
    if (!text) {
        return false;
    }
    return text.split('\n').some(line => LARGE_MARKER.test(line) || SMALL_MARKER.test(line));
}

// Graceful degradation for non-background renders: markdown handles '# ' as a
// heading, but '-# ' would show up verbatim, so only the small marker is
// stripped.
export function stripSizeMarkers(text) {
    if (!text) {
        return '';
    }
    return text.split('\n').map(line => line.replace(SMALL_MARKER, '')).join('\n');
}

export function lineFontSize(line = {}) {
    const em = (SIZE_PROPS[line.size] || SIZE_PROPS.m).em;
    return `${line.emojiOnly ? em * EMOJI_BOOST : em}em`;
}

// Legacy whole-post font size for marker-less posts (unchanged behavior).
export function uniformBackgroundFontSize(text) {
    return (text || '').length < 30 ? '3.5em' : '2.5em';
}

// Shared video check: any file with a stream link or a common video extension
// counts as "has a playable video" — used to gate both background posts (no
// image or video allowed) and story posts (image required, video excluded).
export function hasPlayableVideo(article) {
    return Array.isArray(article?.files)
        ? article.files.some(file => file.s3LinkStream || file.s3Link?.match(/\.(m4v|m4a|mp4|mov)$/i))
        : false;
}

// Single gate for "does this post render as a background card?" — used by
// both the feed (gui/listFrontpage.js) and the composer preview so they can
// never disagree. Mirrors the legacy inline check, with the flat 150-char
// limit replaced by the weighted budget.
export function isBackgroundPost(article) {
    if (!article || !(article.background || article.backgroundImage)) {
        return false;
    }
    const hasImage = article.images?.length > 0;
    if (hasImage || hasPlayableVideo(article)) {
        return false;
    }
    return weightedLength(article.ingress) <= BACKGROUND_TEXT_BUDGET;
}

// Story mode: the post text is drawn over the photo. Needs the author's flag,
// at least one image, no video, no inline widgets ({{poll}} etc. need the
// segment renderer) and a text that fits the same budget as background posts.
export function isStoryPost(article) {
    if (!article || !article.textOverImage) {
        return false;
    }
    if (!(article.images?.length > 0) || hasPlayableVideo(article)) {
        return false;
    }
    const ingress = article.ingress || '';
    if (ingress.includes('{{')) {
        return false;
    }
    return weightedLength(ingress) <= BACKGROUND_TEXT_BUDGET;
}

// Per-line sized rendering applies when the text carries size markers or
// emoji-only lines — but never when inline widget markers ({{poll}} etc.)
// are present, since those need the segment renderer.
export function usesPerLineSizes(text) {
    if (!text || text.includes('{{')) {
        return false;
    }
    return hasSizeMarkers(text) || parseBackgroundLines(text).some(line => line.emojiOnly);
}

// Font styles for background posts (article.backgroundFont). System font
// stacks only — no webfont downloads.
export const BACKGROUND_FONTS = [
    { key: '', label: 'Standard', textKey: 'article.backgroundFontStandard', css: '' },
    {
        key: 'impact', label: 'Poster', textKey: 'article.backgroundFontPoster',
        css: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    },
    {
        key: 'serif', label: 'Classic', textKey: 'article.backgroundFontClassic',
        css: "Georgia, 'Times New Roman', serif",
    },
    {
        key: 'marker', label: 'Marker', textKey: 'article.backgroundFontMarker',
        css: "'Marker Felt', 'Segoe Print', 'Comic Sans MS', cursive",
    },
    {
        key: 'rounded', label: 'Rounded', textKey: 'article.backgroundFontRounded',
        css: "'Arial Rounded MT Bold', 'Trebuchet MS', 'Segoe UI', sans-serif",
    },
    {
        key: 'condensed', label: 'Condensed', textKey: 'article.backgroundFontCondensed',
        css: "'Avenir Next Condensed', 'Arial Narrow', sans-serif-condensed, sans-serif",
    },
    {
        key: 'typewriter', label: 'Typewriter', textKey: 'article.backgroundFontTypewriter',
        css: "'Courier New', Courier, monospace",
    },
    {
        key: 'elegant', label: 'Elegant', textKey: 'article.backgroundFontElegant',
        css: "Didot, 'Bodoni MT', 'Times New Roman', serif",
    },
    {
        key: 'geometric', label: 'Geometric', textKey: 'article.backgroundFontGeometric',
        css: "Futura, 'Century Gothic', Avenir, sans-serif",
    },
    {
        key: 'handwritten', label: 'Handwritten', textKey: 'article.backgroundFontHandwritten',
        css: "'Bradley Hand', 'Segoe Print', 'Comic Sans MS', cursive",
    },
    {
        key: 'slab', label: 'Slab', textKey: 'article.backgroundFontSlab',
        css: "Rockwell, 'Roboto Slab', 'Courier New', serif",
    },
    {
        key: 'friendly', label: 'Friendly', textKey: 'article.backgroundFontFriendly',
        css: "'Trebuchet MS', Verdana, 'Segoe UI', sans-serif",
    },
];

// Open webfonts standing in for the system stacks on the server-rendered
// share card (Linux has no Arial Black / Marker Felt). Only the public card
// route loads them; the feed keeps the system stacks.
export const BACKGROUND_FONT_WEBFONTS = {
    '': 'Inter',
    impact: 'Anton',
    serif: 'Lora',
    marker: 'Caveat',
    rounded: 'Nunito',
    condensed: 'Oswald',
    typewriter: 'Courier Prime',
    elegant: 'Playfair Display',
    geometric: 'Josefin Sans',
    handwritten: 'Kalam',
    slab: 'Zilla Slab',
    friendly: 'Varela Round',
};

export function backgroundFontCss(key, { webfonts = false } = {}) {
    const font = BACKGROUND_FONTS.find(f => f.key === key);
    if (!font) {
        return '';
    }
    if (!webfonts) {
        return font.css;
    }
    const family = BACKGROUND_FONT_WEBFONTS[font.key];
    if (!family) {
        return font.css;
    }
    return font.css ? `'${family}', ${font.css}` : `'${family}', sans-serif`;
}

// No weight axis on purpose: Anton and Varela Round ship a single weight and
// the CSS2 API rejects unknown weights; the card's font-weight 500 is
// synthesized by the browser where a family lacks it.
export function webfontStylesheetUrl() {
    const families = [...new Set(Object.values(BACKGROUND_FONT_WEBFONTS))]
        .map(family => `family=${family.replace(/ /g, '+')}`);
    return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

// Text effects for background posts (article.backgroundTextEffect). Missing or
// unknown values intentionally resolve to outline so existing posts retain the
// legibility improvement introduced before the selector was added.
export const BACKGROUND_TEXT_EFFECTS = [
    { key: 'outline', label: 'Outline', textKey: 'article.backgroundTextEffectOutline' },
    { key: 'strong', label: 'Strong outline', textKey: 'article.backgroundTextEffectStrong' },
    { key: 'soft', label: 'Soft shadow', textKey: 'article.backgroundTextEffectSoft' },
    { key: 'glow', label: 'Glow', textKey: 'article.backgroundTextEffectGlow' },
    { key: 'lift', label: 'Lifted', textKey: 'article.backgroundTextEffectLift' },
    { key: 'none', label: 'None', textKey: 'article.backgroundTextEffectNone' },
];

export function backgroundTextEffectKey(key) {
    return BACKGROUND_TEXT_EFFECTS.some(effect => effect.key === key) ? key : 'outline';
}

const DARK_TEXT_OUTLINE = 'rgba(0,0,0,0.72)';
const LIGHT_TEXT_OUTLINE = 'rgba(255,255,255,0.72)';

// Solid colors and patterned fills for background-post text
// (article.backgroundTextStyle). Pattern CSS lives in custom.css; keeping the
// metadata here gives the composer, full form and feed a shared allowlist.
export const BACKGROUND_TEXT_STYLES = [
    { key: 'auto', label: 'Auto', textKey: 'article.backgroundTextStyleAuto' },
    { key: 'white', label: 'White', textKey: 'article.backgroundTextStyleWhite', color: '#ffffff' },
    {
        key: 'black', label: 'Black', textKey: 'article.backgroundTextStyleBlack',
        color: '#111111', outlineColor: LIGHT_TEXT_OUTLINE,
    },
    { key: 'cream', label: 'Cream', textKey: 'article.backgroundTextStyleCream', color: '#fff1d6' },
    { key: 'gold', label: 'Gold', textKey: 'article.backgroundTextStyleGold', color: '#ffd45c' },
    { key: 'coral', label: 'Coral', textKey: 'article.backgroundTextStyleCoral', color: '#ff9b91' },
    { key: 'mint', label: 'Mint', textKey: 'article.backgroundTextStyleMint', color: '#8ff0cf' },
    { key: 'sky', label: 'Sky', textKey: 'article.backgroundTextStyleSky', color: '#9edcff' },
    { key: 'rainbow', label: 'Rainbow', textKey: 'article.backgroundTextStyleRainbow', pattern: true },
    {
        key: 'gold-gradient', label: 'Golden shine',
        textKey: 'article.backgroundTextStyleGoldGradient', pattern: true,
    },
    {
        key: 'silver-gradient', label: 'Silver shine',
        textKey: 'article.backgroundTextStyleSilverGradient', pattern: true,
    },
    {
        key: 'sunset-gradient', label: 'Sunset',
        textKey: 'article.backgroundTextStyleSunsetGradient', pattern: true,
    },
    {
        key: 'ocean-gradient', label: 'Ocean',
        textKey: 'article.backgroundTextStyleOceanGradient', pattern: true,
    },
    {
        key: 'candy-stripes', label: 'Candy stripes',
        textKey: 'article.backgroundTextStyleCandyStripes', pattern: true,
    },
    {
        key: 'neon-gradient', label: 'Neon',
        textKey: 'article.backgroundTextStyleNeonGradient', pattern: true,
    },
];

export function backgroundTextStyle(key) {
    return BACKGROUND_TEXT_STYLES.find(style => style.key === key) || BACKGROUND_TEXT_STYLES[0];
}

export function backgroundTextStyleKey(key) {
    return backgroundTextStyle(key).key;
}

export function backgroundTextStyleColor(key, automaticColor = 'inherit') {
    return backgroundTextStyle(key).color || automaticColor;
}

export function backgroundTextStyleOutlineColor(key, automaticOutline = DARK_TEXT_OUTLINE) {
    const style = backgroundTextStyle(key);
    if (style.key === 'auto') return automaticOutline;
    return style.outlineColor || DARK_TEXT_OUTLINE;
}

export function backgroundTextStyleIsPattern(key) {
    return backgroundTextStyle(key).pattern === true;
}

// Motion presets for background-post text (article.backgroundTextAnimation).
// CSS honors prefers-reduced-motion so the stored choice never overrides a
// viewer's accessibility preference.
export const BACKGROUND_TEXT_ANIMATIONS = [
    { key: 'none', label: 'No animation', textKey: 'article.backgroundTextAnimationNone' },
    { key: 'wave', label: 'Wave', textKey: 'article.backgroundTextAnimationWave' },
    { key: 'flicker', label: 'Flicker', textKey: 'article.backgroundTextAnimationFlicker' },
    { key: 'pulse', label: 'Pulse', textKey: 'article.backgroundTextAnimationPulse' },
    { key: 'float', label: 'Float', textKey: 'article.backgroundTextAnimationFloat' },
    { key: 'bounce', label: 'Bounce', textKey: 'article.backgroundTextAnimationBounce' },
    { key: 'shimmer', label: 'Shimmer', textKey: 'article.backgroundTextAnimationShimmer' },
    { key: 'glitch', label: 'Glitch', textKey: 'article.backgroundTextAnimationGlitch' },
];

export function backgroundTextAnimationKey(key) {
    return BACKGROUND_TEXT_ANIMATIONS.some(animation => animation.key === key) ? key : 'none';
}

// Decorative edge overlays for background posts (article.backgroundDecoration).
// Symbols are rendered in an aria-hidden, pointer-events-free layer and a fresh
// array is returned to keep these shared definitions immutable.
export const BACKGROUND_DECORATIONS = [
    { key: 'none', label: 'No decoration', textKey: 'article.backgroundDecorationNone', symbols: [] },
    {
        key: 'confetti', label: 'Confetti', textKey: 'article.backgroundDecorationConfetti',
        symbols: ['●', '◆', '▲', '✦', '■', '●', '✦', '▲', '◆', '■', '●', '✦'],
    },
    {
        key: 'sparkles', label: 'Sparkles', textKey: 'article.backgroundDecorationSparkles',
        symbols: ['✦', '✧', '⋆', '✦', '✧', '⋆', '✦', '✧', '⋆', '✦', '✧', '⋆'],
    },
    {
        key: 'hearts', label: 'Hearts', textKey: 'article.backgroundDecorationHearts',
        symbols: ['♥', '♡', '♥', '♡', '♥', '♡', '♥', '♡', '♥', '♡', '♥', '♡'],
    },
    {
        key: 'snow', label: 'Snow', textKey: 'article.backgroundDecorationSnow',
        symbols: ['❄', '❅', '❆', '❄', '❅', '❆', '❄', '❅', '❆', '❄', '❅', '❆'],
    },
    {
        key: 'paws', label: 'Paw prints', textKey: 'article.backgroundDecorationPaws',
        symbols: ['🐾', '🐾', '🐾', '🐾', '🐾', '🐾', '🐾', '🐾', '🐾', '🐾', '🐾', '🐾'],
    },
    {
        key: 'footballs', label: 'Footballs', textKey: 'article.backgroundDecorationFootballs',
        symbols: ['⚽', '⚽', '⚽', '⚽', '⚽', '⚽', '⚽', '⚽', '⚽', '⚽', '⚽', '⚽'],
    },
    {
        key: 'balloons', label: 'Balloons', textKey: 'article.backgroundDecorationBalloons',
        symbols: ['🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈'],
    },
];

export function backgroundDecoration(key) {
    return BACKGROUND_DECORATIONS.find(decoration => decoration.key === key) || BACKGROUND_DECORATIONS[0];
}

export function backgroundDecorationKey(key) {
    return backgroundDecoration(key).key;
}

export function backgroundDecorationSymbols(key) {
    return backgroundDecoration(key).symbols.slice();
}

// Animated layers placed between the selected background and its text
// (article.backgroundMotion). Motion is disabled by CSS when the viewer prefers
// reduced motion, while the layer remains as a quiet static treatment.
export const BACKGROUND_MOTIONS = [
    { key: 'none', label: 'Static background', textKey: 'article.backgroundMotionNone' },
    {
        key: 'flowing-gradient', label: 'Flowing gradient',
        textKey: 'article.backgroundMotionFlowingGradient',
    },
    { key: 'aurora', label: 'Aurora', textKey: 'article.backgroundMotionAurora' },
    {
        key: 'twinkling-stars', label: 'Twinkling stars',
        textKey: 'article.backgroundMotionTwinklingStars',
    },
    {
        key: 'gentle-confetti', label: 'Gentle confetti',
        textKey: 'article.backgroundMotionGentleConfetti',
    },
];

export function backgroundMotionKey(key) {
    return BACKGROUND_MOTIONS.some(motion => motion.key === key) ? key : 'none';
}

// Class list shared by the feed card, the story text layer and the composer
// preview, so the three can never disagree on how a text style is spelled.
export function backgroundTextClasses(article = {}) {
    const style = backgroundTextStyleKey(article.backgroundTextStyle);
    return [
        `background-text-effect-${backgroundTextEffectKey(article.backgroundTextEffect)}`,
        `background-text-style-${style}`,
        `background-text-animation-${backgroundTextAnimationKey(article.backgroundTextAnimation)}`,
        `background-decoration-${backgroundDecorationKey(article.backgroundDecoration)}`,
        `background-motion-${backgroundMotionKey(article.backgroundMotion)}`,
        backgroundTextStyleIsPattern(style) ? 'background-text-pattern' : '',
    ].filter(Boolean);
}

// Irregular, deterministic coordinates avoid the tiled-wallpaper look while
// keeping the editor preview identical to the published feed on every render.
const BACKGROUND_MOTION_PARTICLE_POSITIONS = [
    [3, 11], [13, 63], [7, 87], [22, 27], [18, 76], [31, 8],
    [27, 51], [37, 91], [43, 34], [48, 70], [55, 16], [59, 88],
    [66, 43], [72, 6], [78, 61], [85, 24], [92, 79], [96, 46],
    [35, 61], [52, 96], [63, 72], [75, 37], [88, 94], [10, 39],
];

export function backgroundMotionParticleStyles(key) {
    if (key !== 'twinkling-stars' && key !== 'gentle-confetti') {
        return [];
    }
    return BACKGROUND_MOTION_PARTICLE_POSITIONS.map(([x, y], index) => {
        const size = 2 + ((index * 7) % 5);
        const duration = (key === 'twinkling-stars' ? 2.2 : 8.5) + ((index * 11) % 31) / 10;
        const delay = -(((index * 17) % 83) / 10);
        const drift = -34 + ((index * 23) % 69);
        const returnDrift = -26 + ((index * 29) % 53);
        return [
            `--particle-x: ${x}%`,
            `--particle-y: ${y}%`,
            `--particle-size: ${size}px`,
            `--particle-duration: ${duration}s`,
            `--particle-delay: ${delay}s`,
            `--particle-drift: ${drift}px`,
            `--particle-return-drift: ${returnDrift}px`,
        ].join('; ');
    });
}

// One-tap styles for the composer. `background` is the NAME of an entry in
// BACKGROUND_PICKER_LIST (lib/fieldsHelpers.js) so the gradient CSS is never
// duplicated here; resolvePreset() turns a preset into article field values.
export const BACKGROUND_PRESET_FIELDS = [
    'background',
    'backgroundImage',
    'backgroundFont',
    'backgroundTextEffect',
    'backgroundTextStyle',
    'backgroundTextAnimation',
    'backgroundDecoration',
    'backgroundMotion',
];

export const BACKGROUND_PRESETS = [
    {
        key: 'gratulerer', label: 'Congratulations', textKey: 'article.backgroundPresetGratulerer',
        background: 'Gull', backgroundFont: 'impact', backgroundTextEffect: 'strong',
        backgroundTextStyle: 'white', backgroundTextAnimation: 'none',
        backgroundDecoration: 'confetti', backgroundMotion: 'gentle-confetti',
    },
    {
        key: 'bursdag', label: 'Birthday', textKey: 'article.backgroundPresetBursdag',
        background: 'CandyMesh', backgroundFont: 'rounded', backgroundTextEffect: 'outline',
        backgroundTextStyle: 'auto', backgroundTextAnimation: 'bounce',
        backgroundDecoration: 'balloons', backgroundMotion: 'none',
    },
    {
        key: 'kamp', label: 'Match day', textKey: 'article.backgroundPresetKamp',
        background: 'FireAndIce', backgroundFont: 'condensed', backgroundTextEffect: 'strong',
        backgroundTextStyle: 'white', backgroundTextAnimation: 'pulse',
        backgroundDecoration: 'footballs', backgroundMotion: 'none',
    },
    {
        key: 'heia-norge', label: 'Go Norway', textKey: 'article.backgroundPresetHeiaNorge',
        background: 'NorwayFlag', backgroundFont: 'impact', backgroundTextEffect: 'strong',
        backgroundTextStyle: 'white', backgroundTextAnimation: 'none',
        backgroundDecoration: 'none', backgroundMotion: 'none',
    },
    {
        key: 'nordlys', label: 'Northern lights', textKey: 'article.backgroundPresetNordlys',
        background: 'DeepSpace', backgroundFont: 'elegant', backgroundTextEffect: 'glow',
        backgroundTextStyle: 'auto', backgroundTextAnimation: 'none',
        backgroundDecoration: 'sparkles', backgroundMotion: 'aurora',
    },
    {
        key: 'fest', label: 'Party', textKey: 'article.backgroundPresetFest',
        background: 'Synthwave', backgroundFont: 'impact', backgroundTextEffect: 'none',
        backgroundTextStyle: 'neon-gradient', backgroundTextAnimation: 'flicker',
        backgroundDecoration: 'confetti', backgroundMotion: 'none',
    },
    {
        key: 'vinter', label: 'Winter', textKey: 'article.backgroundPresetVinter',
        background: 'Vinterkveld', backgroundFont: 'slab', backgroundTextEffect: 'soft',
        backgroundTextStyle: 'auto', backgroundTextAnimation: 'none',
        backgroundDecoration: 'snow', backgroundMotion: 'twinkling-stars',
    },
    {
        key: 'sommer', label: 'Summer', textKey: 'article.backgroundPresetSommer',
        background: 'WarmSunrise', backgroundFont: 'friendly', backgroundTextEffect: 'outline',
        backgroundTextStyle: 'auto', backgroundTextAnimation: 'none',
        backgroundDecoration: 'none', backgroundMotion: 'none',
    },
    {
        key: 'kjaerlighet', label: 'Love', textKey: 'article.backgroundPresetKjaerlighet',
        background: 'CosmicRose', backgroundFont: 'handwritten', backgroundTextEffect: 'glow',
        backgroundTextStyle: 'auto', backgroundTextAnimation: 'float',
        backgroundDecoration: 'hearts', backgroundMotion: 'none',
    },
    {
        key: 'rolig', label: 'Calm', textKey: 'article.backgroundPresetRolig',
        background: 'MorningMist', backgroundFont: 'serif', backgroundTextEffect: 'soft',
        backgroundTextStyle: 'auto', backgroundTextAnimation: 'none',
        backgroundDecoration: 'none', backgroundMotion: 'none',
    },
];

export function resolvePreset(preset, pickerList = []) {
    if (!preset) {
        return null;
    }
    const entry = pickerList.find(([, name]) => name === preset.background);
    if (!entry) {
        return null;
    }
    return {
        background: entry[0],
        // The admin form's backgroundImage wins over background in the feed —
        // clear it so the preset is what renders.
        backgroundImage: '',
        backgroundFont: BACKGROUND_FONTS.some(font => font.key === preset.backgroundFont) ? preset.backgroundFont : '',
        backgroundTextEffect: backgroundTextEffectKey(preset.backgroundTextEffect),
        backgroundTextStyle: backgroundTextStyleKey(preset.backgroundTextStyle),
        backgroundTextAnimation: backgroundTextAnimationKey(preset.backgroundTextAnimation),
        backgroundDecoration: backgroundDecorationKey(preset.backgroundDecoration),
        backgroundMotion: backgroundMotionKey(preset.backgroundMotion),
    };
}

export function randomPreset(presets = BACKGROUND_PRESETS, random = Math.random) {
    if (!Array.isArray(presets) || presets.length === 0) {
        return null;
    }
    return presets[Math.floor(random() * presets.length)] || null;
}

export const IMAGE_OVERLAYS = [
    { key: 'none', label: 'No image effect', textKey: 'article.imageOverlayNone' },
    {
        key: 'sparkling-stars', label: 'Sparkling stars',
        textKey: 'article.imageOverlaySparklingStars',
    },
    {
        key: 'celebration-confetti', label: 'Celebration confetti',
        textKey: 'article.imageOverlayCelebrationConfetti',
    },
    {
        key: 'pulsing-hearts', label: 'Pulsing hearts',
        textKey: 'article.imageOverlayPulsingHearts',
    },
    { key: 'falling-snow', label: 'Falling snow', textKey: 'article.imageOverlayFallingSnow' },
    { key: 'soft-light', label: 'Soft moving light', textKey: 'article.imageOverlaySoftLight' },
    { key: 'sticker-party', label: 'Sticker: Party', textKey: 'article.imageOverlayStickerParty' },
    { key: 'sticker-cute', label: 'Sticker: Cute', textKey: 'article.imageOverlayStickerCute' },
    { key: 'sticker-paws', label: 'Sticker: Paw prints', textKey: 'article.imageOverlayStickerPaws' },
    { key: 'sticker-footballs', label: 'Sticker: Football', textKey: 'article.imageOverlayStickerFootballs' },
    {
        key: 'sticker-field-shooting', label: 'Sticker: Field shooting',
        textKey: 'article.imageOverlayStickerFieldShooting',
    },
    {
        key: 'sticker-dog-mushing', label: 'Sticker: Dog mushing',
        textKey: 'article.imageOverlayStickerDogMushing',
    },
    {
        key: 'sticker-fishing', label: 'Sticker: Fishing',
        textKey: 'article.imageOverlayStickerFishing',
    },
    {
        key: 'location-sticker', label: 'Location sticker',
        textKey: 'article.imageOverlayLocationSticker',
    },
    { key: 'text-wow', label: 'Text: WOW!', textKey: 'article.imageOverlayTextWow' },
    { key: 'text-love', label: 'Text: LOVE', textKey: 'article.imageOverlayTextLove' },
    { key: 'text-yay', label: 'Text: YAY!', textKey: 'article.imageOverlayTextYay' },
    { key: 'text-goal', label: 'Text: GOAL!', textKey: 'article.imageOverlayTextGoal' },
    { key: 'text-bullseye', label: 'Text: BULLSEYE!', textKey: 'article.imageOverlayTextBullseye' },
    { key: 'text-go-team', label: 'Text: GO TEAM!', textKey: 'article.imageOverlayTextGoTeam' },
    { key: 'text-maal', label: 'Text: MÅL!', textKey: 'article.imageOverlayTextMaal' },
    { key: 'text-heia', label: 'Text: HEIA!', textKey: 'article.imageOverlayTextHeia' },
    {
        key: 'text-gratulerer', label: 'Text: GRATULERER!',
        textKey: 'article.imageOverlayTextGratulerer',
    },
    { key: 'text-takk', label: 'Text: TAKK!', textKey: 'article.imageOverlayTextTakk' },
];

export function imageOverlayKey(key) {
    return IMAGE_OVERLAYS.some(overlay => overlay.key === key) ? key : 'none';
}

const IMAGE_OVERLAY_PARTICLE_SETTINGS = {
    'sparkling-stars': { count: 18, symbols: ['✦', '✧', '⋆'], duration: 2.2 },
    'celebration-confetti': { count: 24, symbols: [''], duration: 8.5 },
    'pulsing-hearts': { count: 12, symbols: ['♥', '♡'], duration: 2.8 },
    'falling-snow': { count: 20, symbols: ['❄', '❅', '•'], duration: 9.5 },
    'sticker-party': {
        count: 7,
        symbols: ['🥳', '🎉', '🎈', '✨', '🎊', '🎉', '🎈'],
        duration: 3.8,
        fontSizeBase: 32,
        fontSizeRange: 15,
        positions: [[10, 18], [88, 16], [8, 76], [91, 70], [22, 88], [76, 86], [50, 10]],
    },
    'sticker-cute': {
        count: 7,
        symbols: ['😍', '🫶', '🌈', '✨', '💖', '😊', '⭐'],
        duration: 3.2,
        fontSizeBase: 30,
        fontSizeRange: 14,
        positions: [[10, 16], [88, 20], [9, 77], [91, 73], [28, 89], [73, 88], [51, 10]],
    },
    'sticker-paws': {
        count: 8,
        symbols: ['🐾'],
        duration: 4.4,
        fontSizeBase: 26,
        fontSizeRange: 12,
        positions: [[8, 84], [20, 73], [31, 82], [43, 69], [56, 78], [68, 64], [80, 73], [91, 58]],
    },
    'sticker-footballs': {
        count: 6,
        symbols: ['⚽', '⚡', '⚽', '🏆', '⚽', '⚡'],
        duration: 4,
        fontSizeBase: 30,
        fontSizeRange: 15,
        positions: [[9, 19], [89, 18], [8, 79], [91, 76], [27, 89], [75, 88]],
    },
    'sticker-field-shooting': {
        count: 8,
        symbols: ['🎯', '💥', '🏅', '🎯', '✨', '🏆', '🎯', '💥'],
        duration: 3.8,
        fontSizeBase: 28,
        fontSizeRange: 15,
        positions: [[9, 17], [89, 19], [8, 78], [91, 75], [25, 89], [76, 87], [49, 10], [51, 91]],
    },
    'sticker-dog-mushing': {
        count: 9,
        symbols: ['🐕', '🐾', '❄️', '🛷', '🐕', '🏔️', '🐾', '❄️', '🐕'],
        duration: 4.6,
        fontSizeBase: 28,
        fontSizeRange: 15,
        positions: [[8, 19], [20, 86], [89, 17], [88, 78], [32, 90], [51, 10], [67, 88], [9, 72], [92, 48]],
    },
    'sticker-fishing': {
        count: 7,
        symbols: ['🎣', '🐟', '🌊', '🪝', '🎣', '🐟', '🐠'],
        duration: 4.2,
        fontSizeBase: 28,
        fontSizeRange: 14,
        positions: [[9, 18], [89, 20], [8, 78], [91, 74], [24, 89], [75, 88], [50, 10]],
    },
    'text-wow': { count: 1, symbols: ['WOW!'], duration: 2.5, positions: [[50, 18]] },
    'text-love': { count: 1, symbols: ['LOVE'], duration: 3, positions: [[50, 82]] },
    'text-yay': { count: 1, symbols: ['YAY!'], duration: 2.8, positions: [[50, 18]] },
    'text-goal': { count: 1, symbols: ['GOAL!'], duration: 2.6, positions: [[50, 18]] },
    'text-bullseye': { count: 1, symbols: ['BULLSEYE!'], duration: 2.8, positions: [[50, 18]] },
    'text-go-team': { count: 1, symbols: ['GO TEAM!'], duration: 3.2, positions: [[50, 18]] },
    'text-maal': { count: 1, symbols: ['MÅL!'], duration: 2.6, positions: [[50, 18]] },
    'text-heia': { count: 1, symbols: ['HEIA!'], duration: 3.2, positions: [[50, 18]] },
    'text-gratulerer': { count: 1, symbols: ['GRATULERER!'], duration: 2.8, positions: [[50, 18]] },
    'text-takk': { count: 1, symbols: ['TAKK!'], duration: 2.8, positions: [[50, 82]] },
};

export function imageOverlayParticles(key) {
    const settings = IMAGE_OVERLAY_PARTICLE_SETTINGS[key];
    if (!settings) {
        return [];
    }
    const positions = settings.positions || BACKGROUND_MOTION_PARTICLE_POSITIONS;
    return positions.slice(0, settings.count).map(([x, y], index) => {
        const size = 3 + ((index * 7) % 5);
        const fontSize = (settings.fontSizeBase || 13) + ((index * 9) % (settings.fontSizeRange || 16));
        const duration = settings.duration + ((index * 11) % 31) / 10;
        const delay = -(((index * 17) % 83) / 10);
        const drift = -34 + ((index * 23) % 69);
        const returnDrift = -26 + ((index * 29) % 53);
        return {
            symbol: settings.symbols[index % settings.symbols.length],
            style: [
                `--particle-x: ${x}%`,
                `--particle-y: ${y}%`,
                `--particle-size: ${size}px`,
                `--particle-font-size: ${fontSize}px`,
                `--particle-duration: ${duration}s`,
                `--particle-delay: ${delay}s`,
                `--particle-drift: ${drift}px`,
                `--particle-return-drift: ${returnDrift}px`,
            ].join('; '),
        };
    });
}

export const IMAGE_FILTERS = [
    { key: 'none', label: 'Original', textKey: 'article.imageFilterNone' },
    { key: 'vivid', label: 'Vivid', textKey: 'article.imageFilterVivid' },
    { key: 'warm', label: 'Warm', textKey: 'article.imageFilterWarm' },
    { key: 'cool', label: 'Cool', textKey: 'article.imageFilterCool' },
    { key: 'monochrome', label: 'Monochrome', textKey: 'article.imageFilterMonochrome' },
    { key: 'vintage', label: 'Vintage', textKey: 'article.imageFilterVintage' },
    { key: 'cinematic', label: 'Cinematic', textKey: 'article.imageFilterCinematic' },
    { key: 'dramatic', label: 'Dramatic', textKey: 'article.imageFilterDramatic' },
    { key: 'soft', label: 'Soft', textKey: 'article.imageFilterSoft' },
];

export function imageFilterKey(key) {
    return IMAGE_FILTERS.some(filter => filter.key === key) ? key : 'none';
}

// Custom sticker text on images (article.imageStickerText / imageStickerPosition).
// An independent layer, so it combines with any imageOverlay effect.
export const IMAGE_STICKER_MAX_LENGTH = 40;
export const IMAGE_STICKER_POSITIONS = ['bottom', 'top'];

export function cleanImageStickerText(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim().replace(/\s+/g, ' ').slice(0, IMAGE_STICKER_MAX_LENGTH);
}

export function imageStickerPositionKey(key) {
    return IMAGE_STICKER_POSITIONS.includes(key) ? key : 'bottom';
}
